import { useEffect, useMemo, useState } from 'react';

import { useHelpBundle } from '@plasma/ui.application.app';
import Notification from '@plasma/ui.feedback.notification';
import { useResourceBundle, useTranslation } from '@plasma/ui.utils.i18n';

import { fetchAllDataGet_byApiName, fetchAllDataGetFromAppFile_byApiName } from '../../services/gets/getdata';
import {
  getFileByGuid_Post,
  getFileOfPdfChangeover_Post,
  pollForFile,
  sendAllDataPost_byApiNameAndData,
} from '../../services/posts/postdata';
import {
  GuidDataItem,
  GuideInstalledPosition,
  GuideRankedChangesAnalysisResponse,
  GuideSerialSelection,
  GuideSerialSelectionComponent,
  ProfileData,
  RankedGuideRecommendation,
  RequiredGuidesResponse,
} from '../../types/guideMasterDatatype';
import { GuideOrderProps } from './guide-order';
import help from './guide-order.help';
import translations from './i18n';
import { apiUriDesignDrawingsHttp, apiUriGuideDataHttp, apiUriMasterDataHttp } from '../../services/conf/apiConf';

function useGuideOrderHook(props: GuideOrderProps) {
  useResourceBundle(translations, undefined, '2f4b0df9-bf66-4d2c-a88c-fbc22a3db65f');
  useHelpBundle(help);

  const { t } = useTranslation();
  const isAdmin = sessionStorage.getItem('superUserRole');

  const [getProfileData, setGetProfileData] = useState<ProfileData[]>([]);
  const [getAllGuidData, setGetAllGuidData] = useState<GuidDataItem[]>([]);
  const [getRequiredGuideData, setGetRequiredGuideData] = useState<RequiredGuidesResponse | null>(null);
  const [getRankedGuideRecommendations, setGetRankedGuideRecommendations] = useState<
    RankedGuideRecommendation[] | null
  >(null);
  const [requiredGuidesLoading, setRequiredGuidesLoading] = useState<boolean>(false);
  const [currentProfile, setCurrentProfile] = useState<string | null>(null);
  const [targetProfile, setTargetProfile] = useState<string | null>(null);
  const [selectedStand, setSelectedStand] = useState<string | null>(null);
  const [getAllGuideStand, setGetAllGuideStand] = useState<string[] | null>(null);
  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const [iFrameSrc, setIframeSrc] = useState('');
  const [showConsumableCodeDrawing, setshowConsumableCodeDrawing] = useState(false);
  const [selectedCode, setSelectedCode] = useState<string>('');
  const [selectedDrawingGuid, setSelectedDrawingGuid] = useState<string>(''); // guid of the drawing shown in the viewer
  const [viewDrawing, setviewDrawing] = useState(false);
  const [expandedSerialCode, setExpandedSerialCode] = useState<string | null>(null);

  const [selectedEntrySerial, setSelectedEntrySerial] = useState<string | null>(null);
  const [selectedExitSerial, setSelectedExitSerial] = useState<string | null>(null);
  const [savedSelections, setSavedSelections] = useState<GuideSerialSelection[]>([]);
  const [guideReportSrc, setGuideReportSrc] = useState<string>(''); // PDF of the guide report, shown in a modal

  const [installedPositions, setInstalledPositions] = useState<GuideInstalledPosition[]>([]);
  const [installedPositionsLoaded, setInstalledPositionsLoaded] = useState<boolean>(false);
  const [showInstalledPositionForm, setShowInstalledPositionForm] = useState<boolean>(false);

  const isInstalledPositionsEmpty = installedPositionsLoaded && installedPositions.length === 0;

  useEffect(() => {
    fetchData();
    fetchInstalledPositions();
  }, []);

  useEffect(() => {
    if (installedPositions.length > 0) {
      const installedProfile = installedPositions.find((item) => item.productDescription);
      if (installedProfile) {
        const formatted = `${installedProfile.productType} ${installedProfile.productDescription}`;
        setCurrentProfile((prev) => (prev === null ? formatted : prev));
      }
    }
  }, [installedPositions]);

  useEffect(() => {
    fetchGuideData();
    fetchSavedSelections();
  }, [targetProfile]);

  const fetchData = async () => {
    setPageLoading(true);
    try {
      const profileData = (await fetchAllDataGet_byApiName(
        'GetMasterProductDetails',
        apiUriMasterDataHttp(),
      )) as ProfileData[];
      const GuidData = (await fetchAllDataGetFromAppFile_byApiName(
        'GetConsumableItemDocumentGuid',
        apiUriDesignDrawingsHttp(),
      )) as GuidDataItem[];

      setGetProfileData(profileData);
      setGetAllGuidData(GuidData);
    } catch (error) {
      console.error('Failed to Fetch Profile Options list', error);
    } finally {
      setPageLoading(false);
    }
  };

  const fetchGuideData = async () => {
    setRequiredGuidesLoading(true);
    setPageLoading(true);
    try {
      const targetProfileQuery = encodeURIComponent(targetProfile ?? '');
      const currentProfileQuery = encodeURIComponent(currentProfile?.split(' ')[1] ?? '');

      const requiredGuides = (await fetchAllDataGet_byApiName(
        `GetRequiredGuides?productDescription=${targetProfileQuery}`,
        apiUriGuideDataHttp(),
      )) as RequiredGuidesResponse;

      const allStands = requiredGuides.stands.map((item) => item.standName);

      const rankedGuideRecommendations = (await fetchAllDataGet_byApiName(
        `GetRankedGuideRecommendations?targetProfile=${targetProfileQuery}&currentProfile=${currentProfileQuery}`,
        apiUriGuideDataHttp(),
      )) as GuideRankedChangesAnalysisResponse;

      setGetRequiredGuideData(requiredGuides);
      setGetAllGuideStand(allStands);
      setGetRankedGuideRecommendations(rankedGuideRecommendations.recommendations);
    } catch (error) {
      console.error('Failed to fetch Required Guides', error);
      setGetRequiredGuideData(null);
      setGetAllGuideStand([]);
      setGetRankedGuideRecommendations([]);
    } finally {
      setRequiredGuidesLoading(false);
      setPageLoading(false);
    }
  };

  // Serials saved earlier for the target profile, so the screen shows what was already selected
  const fetchSavedSelections = async () => {
    if (!targetProfile) {
      setSavedSelections([]);
      return;
    }
    try {
      const selections = (await fetchAllDataGet_byApiName(
        `GetGuideSerialSelections?targetProfile=${encodeURIComponent(targetProfile)}`,
        apiUriGuideDataHttp(),
      )) as GuideSerialSelection[];
      setSavedSelections(selections || []);
    } catch (error) {
      console.error('Failed to fetch saved guide serial selections', error);
      setSavedSelections([]);
    }
  };

  const fetchInstalledPositions = async () => {
    try {
      const positions = (await fetchAllDataGet_byApiName(
        'GetGuideInstalledPositions',
        apiUriGuideDataHttp(),
      )) as GuideInstalledPosition[];
      setInstalledPositions(positions || []);
    } catch (error) {
      console.error('Failed to fetch guide installed positions', error);
      setInstalledPositions([]);
    } finally {
      setInstalledPositionsLoaded(true);
    }
  };

  // Active stand data from GetRequiredGuides
  const activeStandTargetComponentData = useMemo(() => {
    if (!getRequiredGuideData || !selectedStand) return null;
    return getRequiredGuideData.stands?.find((stand) => stand.standName === selectedStand) ?? null;
  }, [getRequiredGuideData, selectedStand]);

  const activeStandSerialRecommendation = useMemo(() => {
    if (!getRankedGuideRecommendations || !selectedStand) return null;
    const matchedStand = getRankedGuideRecommendations.filter(
      (data: RankedGuideRecommendation) => data.standName === selectedStand,
    );

    return {
      entry: matchedStand.find((item) => item.guideName?.toLowerCase().includes('entry')) ?? null,
      exit: matchedStand.find((item) => item.guideName?.toLowerCase().includes('exit')) ?? null,
    };
  }, [getRankedGuideRecommendations, selectedStand]);

  // Tick the saved serials when a stand is shown
  useEffect(() => {
    const savedFor = (type: 'entry' | 'exit') =>
      savedSelections.find((item) => item.standName === selectedStand && item.guideName?.toLowerCase().includes(type))
        ?.serialNumber ?? null;

    setSelectedEntrySerial(savedFor('entry'));
    setSelectedExitSerial(savedFor('exit'));
  }, [savedSelections, selectedStand]);

  // Product type of the selected target profile (the Select's value is the product description)
  const targetProfileProducttype = useMemo(
    () => getProfileData.find((profile) => profile.productdescription === targetProfile)?.producttype,
    [getProfileData, targetProfile],
  );

  // The selection for one guide position of the active stand, with the selected serial's components as shown in
  // its suggestion card. No serial selected means the position's saved selection is cleared.
  const buildSerialSelection = (type: 'entry' | 'exit'): GuideSerialSelection | null => {
    const recommendation = activeStandSerialRecommendation?.[type];
    const requiredGuide =
      type === 'entry' ? activeStandTargetComponentData?.entryGuide : activeStandTargetComponentData?.exitGuide;
    const guideName = recommendation?.guideName ?? requiredGuide?.guideName;
    if (!selectedStand || !targetProfile || !guideName) return null;

    const serialNumber = type === 'entry' ? selectedEntrySerial : selectedExitSerial;
    const changeAnalysis = recommendation?.changeAnalysis;
    const serialData = [recommendation?.recommendedSerial, changeAnalysis?.currentSerial].find(
      (item) => item?.serial?.componentCode === serialNumber,
    );
    // A saved serial that is not one of the current suggestions keeps the components it was saved with
    const savedSerial = savedSelections.find(
      (item) =>
        item.standName === selectedStand &&
        item.guideName?.toLowerCase().includes(type) &&
        item.serialNumber === serialNumber,
    );
    const serialComponents =
      serialData?.serialComponents ??
      (savedSerial?.components ?? [])
        .filter((component) => component.componentCode)
        .map((component) => ({
          componentDescription: component.componentDescription,
          componentCode: component.componentCode ?? '',
        }));

    const changes = new Map(
      (changeAnalysis?.componentChanges ?? []).map((change) => [change.componentDescription, change]),
    );
    const descriptions = Array.from(
      new Set([...serialComponents.map((component) => component.componentDescription), ...Array.from(changes.keys())]),
    ).filter(Boolean);

    const components: GuideSerialSelectionComponent[] = serialNumber
      ? descriptions.map((description) => {
          const change = changes.get(description);
          return {
            componentDescription: description,
            componentCode:
              serialComponents.find((component) => component.componentDescription === description)?.componentCode ??
              null,
            currentCode: change?.currentCode ?? null,
            requiredCode: change?.requiredCode ?? null,
            changeType: change?.changeType ?? null,
          };
        })
      : [];

    return {
      standName: selectedStand,
      guideName,
      targetProfile,
      targetProfileProducttype: targetProfileProducttype ?? null,
      serialNumber,
      guideTypeCode: requiredGuide?.guideTypeCode ?? null,
      isRecommended: !!serialNumber && recommendation?.recommendedSerial?.serial?.componentCode === serialNumber,
      totalChangesCount: changeAnalysis?.totalChangesCount ?? 0,
      currentProfile,
      components,
    };
  };

  const saveSerialSelection = async () => {
    const selections = [buildSerialSelection('entry'), buildSerialSelection('exit')].filter(
      (item): item is GuideSerialSelection => item !== null,
    );
    if (selections.length === 0) return;

    setPageLoading(true);
    try {
      const saved = (await sendAllDataPost_byApiNameAndData(
        'SaveGuideSerialSelections',
        selections,
        apiUriGuideDataHttp(),
      )) as GuideSerialSelection[];
      setSavedSelections(saved || []);
      Notification.success(t('saved'), t('guide-serial-selection-saved'), undefined, 'medium', 3);
    } catch (error) {
      console.error('Failed to save guide serial selection', error);
      Notification.error(t('error'), t('guide-serial-selection-save-failed'), undefined, 'medium', 3);
    } finally {
      setPageLoading(false);
    }
  };

  const handleClearEntrySelection = () => {
    setSelectedEntrySerial(null);
  };

  const handleClearExitSelection = () => {
    setSelectedExitSerial(null);
  };

  const handleOpenInstalledPositionForm = () => {
    setShowInstalledPositionForm(true);
  };

  const handleCloseInstalledPositionForm = () => {
    setShowInstalledPositionForm(false);
  };

  const handleInstalledPositionsSaved = (positions: GuideInstalledPosition[]) => {
    setInstalledPositions(positions || []);
    setInstalledPositionsLoaded(true);
    setShowInstalledPositionForm(false);

    const match = positions?.find((item) => item.productDescription);
    const updatedProfile = match ? `${match.productType} ${match.productDescription}` : null;
    setCurrentProfile(updatedProfile);
  };

  const handleTargetProfileSelection = (value: string | null) => {
    setTargetProfile(value);
  };

  const handleStandSelection = (value: string | null) => {
    setSelectedStand(value);
  };

  const handleDeletePDF = () => {
    if (!selectedDrawingGuid) {
      return;
    }

    const queryParams = new URLSearchParams({
      guid: selectedDrawingGuid,
    });

    getFileByGuid_Post(`DeleteConsumableItemDesign?${queryParams}`, {}, apiUriDesignDrawingsHttp())
      .then((response) => {
        if (response) {
          fetchData();
          setIframeSrc('');
          setSelectedDrawingGuid('');
          setshowConsumableCodeDrawing(false);
          Notification.success('Removed !', 'File Deleted Permanently', undefined, 'medium', 3);
          fetchData();
        }
      })
      .catch((error) => {
        console.log(error);
        Notification.error('Error !', 'Unsuccessful Attempt', undefined, 'medium', 3);
      });
  };

  const handleDrawingModalCancel = () => {
    setIframeSrc('');
    setSelectedCode('');
    setSelectedDrawingGuid('');
    setshowConsumableCodeDrawing(false);
  };

  const handleConsumableCodeClick = (code: string) => {
    setSelectedCode(code);
    handleDrawingModal(code);
  };

  async function fetchAndSetIframeSource(guId: string | undefined, apiName: string, baseUri?: string) {
    setPageLoading(true);
    try {
      const queryParams = new URLSearchParams({ guid: guId ?? '' });
      const blob = await pollForFile(`${apiName}?${queryParams}`, {}, undefined, undefined, baseUri);

      // pollForFile can return undefined; guard before passing to URL.createObjectURL
      // (which only accepts Blob | MediaSource) so TypeScript and runtime are both safe.
      if (!blob) {
        throw new Error('No file returned from server');
      }

      const blobUrl = URL.createObjectURL(blob);
      setIframeSrc(blobUrl);
      setSelectedDrawingGuid(guId ?? '');
      setshowConsumableCodeDrawing(true);
    } catch (error) {
      console.error('Error during API call:', error);
      Notification.error(t('error'), t('failed-loading-drawing'));
    } finally {
      setPageLoading(false);
    }
  }

  const handleDrawingModal = (code: string) => {
    const guidForSelectedCode: any = getAllGuidData?.find((item: GuidDataItem) => item.docfor === code);
    const drawingGuids = guidForSelectedCode?.guid || []; //Extract out the GUID string array

    if (!guidForSelectedCode || drawingGuids.length === 0 || !drawingGuids[0]) {
      Notification.info(t('warning'), t('no-drawing-found'));
      setSelectedCode('');
      return;
    }
    fetchAndSetIframeSource(drawingGuids[0], 'getConsumableItemDesign', apiUriDesignDrawingsHttp());
  };

  // Product Change report rows for the selected stand: for each ticked entry/exit serial, the serial against the target
  // guide type, then all the rows of its suggestion card with their current (Code 1) and target (Code 2) code,
  // changed or not.
  const buildGuideReportRows = () => {
    const productProfile1 = currentProfile?.replace(' ', '-') ?? '';
    const productProfile2 = [targetProfileProducttype, targetProfile].filter(Boolean).join('-');

    return (['entry', 'exit'] as const)
      .flatMap((type) => {
        const selection = buildSerialSelection(type);

        if (!selection?.serialNumber) return [];

        // Target guide type: from the stand's Entry/Exit Guide Components panel for the target profile
        const targetGuide =
          type === 'entry' ? activeStandTargetComponentData?.entryGuide : activeStandTargetComponentData?.exitGuide;

        const row = (
          consumable: string,
          currentCode: string | null | undefined,
          targetCode: string | null | undefined,
        ) => ({
          productProfile1,
          productProfile2,
          area: selection.standName,
          zone: selection.guideName,
          equipmentCode: selection.serialNumber,
          drawingNumber: selection.guideTypeCode ?? '',
          consumable,
          consumableCode1: currentCode || '-',
          consumableCode2: targetCode || '-',
        });

        // Every row of the ticked serial's suggestion card, in the card's order and read the way the card shows it:
        // a changed component ("current → target", e.g. Top Insert 15948134 → 15819280) gives its current and required
        // code; an unchanged one shows a single code, which is then both current and target
        return [
          row('Guide Type', selection.serialNumber, targetGuide?.guideTypeCode ?? selection.guideTypeCode),
          ...selection.components.map((component) => {
            const isChanged =
              selection.totalChangesCount > 0 && !!component.changeType && component.changeType !== 'Unchanged';
            return isChanged
              ? row(component.componentDescription, component.currentCode, component.requiredCode)
              : row(component.componentDescription, component.componentCode, component.componentCode);
          }),
        ];
      })
      .map((item, index) => ({ ...item, rowIndex: index, displaySeq: index + 1 }));
  };

  const generateGuideReport = async () => {
    if (!selectedEntrySerial && !selectedExitSerial) {
      Notification.info(t('warning'), t('select-guide-serial-for-report'));
      return;
    }

    const rows = buildGuideReportRows();
    if (rows.length === 0) {
      Notification.info(t('warning'), t('no-guide-changes-for-report'));
      return;
    }

    setPageLoading(true);
    try {
      const pdf = await getFileOfPdfChangeover_Post('GenerateProductChangeReport', rows, apiUriGuideDataHttp());
      setGuideReportSrc(pdf);
    } catch (error) {
      console.error('Error during API call:', error);
      Notification.error(t('error'), t('guide-report-failed'));
    } finally {
      setPageLoading(false);
    }
  };

  const handleGuideReportCancel = () => {
    setGuideReportSrc('');
  };

  return {
    t,
    pageLoading,
    isAdmin,
    getProfileData,
    setGetProfileData,
    getAllGuidData,
    setGetAllGuidData,
    getAllGuideStand,
    setGetAllGuideStand,
    selectedStand,
    setSelectedStand,
    getRequiredGuideData,
    requiredGuidesLoading,
    targetProfile,
    setTargetProfile,
    currentProfile,
    setCurrentProfile,
    iFrameSrc,
    setIframeSrc,
    showConsumableCodeDrawing,
    setshowConsumableCodeDrawing,
    viewDrawing,
    setviewDrawing,
    selectedCode,
    setSelectedCode,
    installedPositions,
    installedPositionsLoaded,
    isInstalledPositionsEmpty,
    showInstalledPositionForm,
    handleOpenInstalledPositionForm,
    handleCloseInstalledPositionForm,
    handleInstalledPositionsSaved,
    handleTargetProfileSelection,
    handleConsumableCodeClick,
    handleDeletePDF,
    handleDrawingModalCancel,
    handleStandSelection,
    activeStandTargetComponentData,
    getRankedGuideRecommendations,
    activeStandSerialRecommendation,
    expandedSerialCode,
    setExpandedSerialCode,
    selectedEntrySerial,
    setSelectedEntrySerial,
    selectedExitSerial,
    setSelectedExitSerial,
    handleClearEntrySelection,
    handleClearExitSelection,
    savedSelections,
    saveSerialSelection,
    generateGuideReport,
    guideReportSrc,
    handleGuideReportCancel,
  };
}

export default useGuideOrderHook;
